import { Extension, Mark } from '@tiptap/core';
import { Plugin, PluginKey } from '@tiptap/pm/state';
import Strike from '@tiptap/extension-strike';

// StarterKit's default Strike parses <del> as a strike mark. Our SuggestionDelete
// also uses <del data-sid>. Without this override, <del data-sid> on reload
// receives BOTH marks — rejecting the suggestion removes suggestionDelete but
// leaves strike behind, so the text keeps its strikethrough.
const StrikeWithoutSuggestion = Strike.extend({
  parseHTML() {
    return [
      { tag: 's' },
      { tag: 'del', getAttrs: (el) => el.hasAttribute('data-sid') ? false : null },
      { tag: 'strike' },
    ];
  },
});

const SuggestionDeleteMark = Mark.create({
  name: 'suggestionDelete',
  inclusive: false,

  addAttributes() {
    return {
      suggestionId: {
        default: null,
        // Normalize to string so HTML-parsed IDs ("5") match JSON numeric IDs (5)
        parseHTML: el => el.getAttribute('data-sid') ?? null,
        renderHTML: attrs => attrs.suggestionId != null ? { 'data-sid': String(attrs.suggestionId) } : {},
      },
    };
  },

  parseHTML() { return [{ tag: 'del[data-sid]' }]; },
  renderHTML({ HTMLAttributes }) {
    return ['del', { ...HTMLAttributes, class: 'suggestion-delete' }];
  },
});

const SuggestionInsertMark = Mark.create({
  name: 'suggestionInsert',
  inclusive: false,

  addAttributes() {
    return {
      suggestionId: {
        default: null,
        parseHTML: el => el.getAttribute('data-sid') ?? null,
        renderHTML: attrs => attrs.suggestionId != null ? { 'data-sid': String(attrs.suggestionId) } : {},
      },
    };
  },

  parseHTML() { return [{ tag: 'ins[data-sid]' }]; },
  renderHTML({ HTMLAttributes }) {
    return ['ins', { ...HTMLAttributes, class: 'suggestion-insert' }];
  },
});

/**
 * SuggestionExtension — track-changes suggest mode.
 *
 * Marks: suggestionDelete (<del>), suggestionInsert (<ins>)
 * Both carry a `suggestionId` attr (always stored as string).
 *
 * Commands:
 *   setSuggestMode(bool)
 *   addSuggestion({ id, from, to, suggestedText })
 *   addPendingSuggestionHighlight(from, to)
 *   removePendingSuggestionMarks()
 *   acceptSuggestion(id)
 *   rejectSuggestion(id)
 *   renameSuggestion(oldId, newId)
 */
const SuggestionExtension = Extension.create({
  name: 'suggestion',

  addOptions() {
    return { suggestModeRef: null, isReviewerRef: null };
  },

  addExtensions() {
    return [SuggestionDeleteMark, SuggestionInsertMark];
  },

  addStorage() {
    return { suggestMode: false };
  },

  addCommands() {
    return {

      setSuggestMode:
        (enabled) => () => {
          this.storage.suggestMode = enabled;
          return true;
        },

      // Mark original range as deleted; insert suggested text inheriting the
      // original's formatting marks (bold, color, etc.) so it looks in-context.
      addSuggestion:
        ({ id, from, to, suggestedText }) =>
        ({ tr, state, dispatch }) => {
          const sid = String(id);
          const deleteMark = state.schema.marks.suggestionDelete;
          const insertMark = state.schema.marks.suggestionInsert;
          if (!deleteMark || !insertMark) return false;

          tr.addMark(from, to, deleteMark.create({ suggestionId: sid }));

          let inheritedMarks = [];
          state.doc.nodesBetween(from, to, (node) => {
            if (node.isText && inheritedMarks.length === 0) {
              inheritedMarks = node.marks.filter(
                m => m.type !== deleteMark && m.type !== insertMark
              );
            }
          });

          // Keep a separator between the struck original and the insertion
          const prevChar = state.doc.textBetween(Math.max(from, to - 1), to);
          const textToInsert = (!prevChar || /\s$/.test(prevChar) || /^\s/.test(suggestedText))
            ? suggestedText
            : ` ${suggestedText}`;

          tr.insert(to, state.schema.text(textToInsert, [
            ...inheritedMarks,
            insertMark.create({ suggestionId: sid }),
          ]));

          if (dispatch) dispatch(tr);
          return true;
        },

      // Delete the original text; re-insert the suggested text stripped of its
      // insert mark (preserving any other formatting marks like bold, color).
      acceptSuggestion:
        (suggestionId) =>
        ({ tr, state, dispatch }) => {
          const deleteMark = state.schema.marks.suggestionDelete;
          const insertMark = state.schema.marks.suggestionInsert;
          if (!deleteMark || !insertMark) return false;

          const sid = String(suggestionId);
          const deleteRanges = [];
          const insertRanges = [];

          state.doc.descendants((node, pos) => {
            if (!node.isText) return;
            node.marks.forEach(mark => {
              if (mark.type === deleteMark && String(mark.attrs.suggestionId) === sid)
                deleteRanges.push({ from: pos, to: pos + node.nodeSize });
              if (mark.type === insertMark && String(mark.attrs.suggestionId) === sid)
                insertRanges.push({ from: pos, to: pos + node.nodeSize, node });
            });
          });

          if (deleteRanges.length === 0 && insertRanges.length === 0) return false;

          const insertFragments = insertRanges
            .sort((a, b) => a.from - b.from)
            .map(({ node }) => {
              const otherMarks = node.marks.filter(m => m.type !== insertMark);
              return state.schema.text(node.text, otherMarks.length ? otherMarks : undefined);
            });

          const insertAt = Math.min(...[...deleteRanges, ...insertRanges].map(r => r.from));

          [...deleteRanges, ...insertRanges]
            .sort((a, b) => b.from - a.from)
            .forEach(({ from, to }) => tr.delete(from, to));

          if (insertFragments.length > 0) {
            const pos = tr.mapping.map(insertAt);
            insertFragments.forEach(node => tr.insert(pos, node));
          }

          if (dispatch) dispatch(tr);
          return true;
        },

      // Delete the inserted suggestion text; strip the delete mark (restore original).
      rejectSuggestion:
        (suggestionId) =>
        ({ tr, state, dispatch }) => {
          const deleteMark = state.schema.marks.suggestionDelete;
          const insertMark = state.schema.marks.suggestionInsert;
          if (!deleteMark || !insertMark) return false;

          const sid = String(suggestionId);
          const deleteRanges = [];
          const insertRanges = [];

          state.doc.descendants((node, pos) => {
            if (!node.isText) return;
            node.marks.forEach(mark => {
              if (mark.type === deleteMark && String(mark.attrs.suggestionId) === sid)
                deleteRanges.push({ from: pos, to: pos + node.nodeSize, mark });
              if (mark.type === insertMark && String(mark.attrs.suggestionId) === sid)
                insertRanges.push({ from: pos, to: pos + node.nodeSize });
            });
          });

          if (deleteRanges.length === 0 && insertRanges.length === 0) return false;

          // Delete inserted text first (high → low so offsets stay stable)
          insertRanges
            .sort((a, b) => b.from - a.from)
            .forEach(({ from, to }) => tr.delete(from, to));

          // Then remove delete marks, mapping positions through the transaction.
          // Use the specific mark instance for attribute-exact removal.
          // Also strip any `strike` mark that legacy saves may have baked in
          // (pre-fix, <del data-sid> was double-parsed as suggestionDelete + strike).
          const strikeMark = state.schema.marks.strike;
          deleteRanges.forEach(({ from, to, mark }) => {
            const mappedFrom = tr.mapping.map(from);
            const mappedTo = tr.mapping.map(to);
            if (mappedFrom < mappedTo) {
              tr.removeMark(mappedFrom, mappedTo, mark);
              if (strikeMark) tr.removeMark(mappedFrom, mappedTo, strikeMark);
            }
          });

          if (dispatch) dispatch(tr);
          return true;
        },

      // Highlight range with a pending delete mark while the creation card is open.
      addPendingSuggestionHighlight:
        (from, to) =>
        ({ tr, state, dispatch }) => {
          const deleteMark = state.schema.marks.suggestionDelete;
          if (!deleteMark) return false;
          tr.addMark(from, to, deleteMark.create({ suggestionId: 'pending' }));
          if (dispatch) dispatch(tr);
          return true;
        },

      // Remove pending highlight when the creation card is cancelled.
      removePendingSuggestionMarks:
        () =>
        ({ tr, state, dispatch }) => {
          const deleteMark = state.schema.marks.suggestionDelete;
          if (!deleteMark) return false;
          let changed = false;
          state.doc.descendants((node, pos) => {
            if (!node.isText) return;
            node.marks.forEach(mark => {
              if (mark.type === deleteMark && mark.attrs.suggestionId === 'pending') {
                tr.removeMark(pos, pos + node.nodeSize, mark.type);
                changed = true;
              }
            });
          });
          if (changed && dispatch) dispatch(tr);
          return true;
        },

      // Rename marks in-place (tempId → permanentId) without shifting positions.
      renameSuggestion:
        (oldId, newId) =>
        ({ tr, state, dispatch }) => {
          const deleteMark = state.schema.marks.suggestionDelete;
          const insertMark = state.schema.marks.suggestionInsert;
          if (!deleteMark || !insertMark) return false;

          const oldSid = String(oldId);
          const newSid = String(newId);
          let changed = false;

          state.doc.descendants((node, pos) => {
            if (!node.isText) return;
            node.marks.forEach(mark => {
              if (
                (mark.type === deleteMark || mark.type === insertMark) &&
                String(mark.attrs.suggestionId) === oldSid
              ) {
                tr.removeMark(pos, pos + node.nodeSize, mark.type);
                tr.addMark(pos, pos + node.nodeSize, mark.type.create({ suggestionId: newSid }));
                changed = true;
              }
            });
          });

          if (changed && dispatch) {
            dispatch(tr);
            return true;
          }
          return false;
        },

    };
  },

  // Block direct text input for reviewers — they can only add comments/suggestions via the selection bubble
  addProseMirrorPlugins() {
    const { suggestModeRef, isReviewerRef } = this.options;
    if (!suggestModeRef || !isReviewerRef) return [];

    return [
      new Plugin({
        key: new PluginKey('reviewerInputBlock'),
        props: {
          // Block typing
          handleTextInput(view, from, to, text) {
            return !!(suggestModeRef.current && isReviewerRef.current);
          },
          // Block content-modifying keys (Backspace, Delete, Enter) — allow navigation & copy
          handleKeyDown(view, event) {
            if (!suggestModeRef.current || !isReviewerRef.current) return false;
            // Allow Ctrl/Cmd+C (copy) and Ctrl/Cmd+A (select all)
            if (event.ctrlKey || event.metaKey) {
              const key = event.key.toLowerCase();
              if (key === 'c' || key === 'a') return false;
              return true; // Block Ctrl+V, Ctrl+B, Ctrl+X, etc.
            }
            // Allow navigation keys (arrows, Home, End, etc.)
            if (event.key.length > 1 && !['Backspace', 'Delete', 'Enter'].includes(event.key)) return false;
            // Block Backspace, Delete, Enter, and all character keys
            return true;
          },
          // Block paste
          handlePaste(view, event, slice) {
            return !!(suggestModeRef.current && isReviewerRef.current);
          },
          // Block drag-and-drop
          handleDrop(view, event, slice, moved) {
            return !!(suggestModeRef.current && isReviewerRef.current);
          },
        },
      }),
    ];
  },

});

export default SuggestionExtension;
export { SuggestionDeleteMark, SuggestionInsertMark, StrikeWithoutSuggestion };
